import { parseJsonObject } from "./audio-proxy-protocol";

export interface AudioProxyCloseEvent {
  code?: number;
  reason?: string;
  wasClean?: boolean;
}

export interface AudioProxyConnectionHandlers {
  onOpen(): void;
  onMessage(message: string): void;
  onClose(event: AudioProxyCloseEvent): void;
  onError(error: Error): void;
}

export interface AudioProxyConnection {
  readonly bufferedAmount: number;
  send(message: string): void;
  close(code?: number, reason?: string): void;
}

export interface AudioProxyTransport {
  connect(
    url: string,
    handlers: AudioProxyConnectionHandlers,
  ): AudioProxyConnection;
}

interface WebSocketLike {
  readonly bufferedAmount: number;
  onopen: ((event: Event) => void) | null;
  onmessage: ((event: MessageEvent) => void) | null;
  onclose: ((event: CloseEvent) => void) | null;
  onerror: ((event: Event) => void) | null;
  send(data: string): void;
  close(code?: number, reason?: string): void;
}

export type WebSocketFactory = (url: string) => WebSocketLike;

/** Browser WebSocket adapter kept separate from the streaming state machine. */
export class WebSocketAudioProxyTransport implements AudioProxyTransport {
  constructor(
    private readonly createWebSocket: WebSocketFactory = (url) =>
      new WebSocket(url),
  ) {}

  connect(
    url: string,
    handlers: AudioProxyConnectionHandlers,
  ): AudioProxyConnection {
    const socket = this.createWebSocket(url);
    socket.onopen = () => handlers.onOpen();
    socket.onmessage = (event) => {
      if (typeof event.data === "string") {
        handlers.onMessage(event.data);
      } else {
        handlers.onError(
          new Error("Audio proxy sent a non-text WebSocket message."),
        );
      }
    };
    socket.onclose = (event) =>
      handlers.onClose({
        code: event.code,
        reason: event.reason,
        wasClean: event.wasClean,
      });
    socket.onerror = () =>
      handlers.onError(new Error("Audio proxy WebSocket failed."));

    return {
      get bufferedAmount() {
        return socket.bufferedAmount;
      },
      send(message: string) {
        socket.send(message);
      },
      close(code?: number, reason?: string) {
        socket.close(code, reason);
      },
    };
  }
}

export interface MockAudioProxyTransportOptions {
  /** Defaults to true and is deferred to a microtask. */
  autoOpen?: boolean;
  /** Defaults to true and emulates start_session/session_started. */
  autoSessionStarted?: boolean;
  /** When provided, start_session keys must match exactly. */
  expectedApiKey?: string;
  sessionIdFactory?: () => string;
}

/** Metadata only: frame PCM/base64 is never retained by the mock. */
export interface MockMessageMetadata {
  type?: string;
  proxySessionId?: string;
  sequenceNumber?: number;
  byteLength?: number;
}

interface MockProxySession {
  totalBytes: number;
  ended: boolean;
  attached?: MockAudioProxyConnection;
}

/**
 * A deterministic in-memory implementation of the exact LongformAudioProxy
 * message semantics. It is for development/tests only and retains metadata,
 * never PCM or base64 content.
 */
export class MockAudioProxyTransport implements AudioProxyTransport {
  readonly connections: MockAudioProxyConnection[] = [];
  readonly messages: MockMessageMetadata[] = [];
  private readonly sessions = new Map<string, MockProxySession>();
  private readonly autoOpen: boolean;
  private readonly autoSessionStarted: boolean;
  private readonly expectedApiKey?: string;
  private readonly sessionIdFactory: () => string;
  private sessionCounter = 0;

  constructor(options: MockAudioProxyTransportOptions = {}) {
    this.autoOpen = options.autoOpen ?? true;
    this.autoSessionStarted = options.autoSessionStarted ?? true;
    this.expectedApiKey = options.expectedApiKey;
    this.sessionIdFactory =
      options.sessionIdFactory ?? (() => `mock-proxy-session-${++this.sessionCounter}`);
  }

  connect(
    _url: string,
    handlers: AudioProxyConnectionHandlers,
  ): AudioProxyConnection {
    const connection = new MockAudioProxyConnection(
      handlers,
      (message) => this.recordMessage(connection, message),
      () => this.detach(connection),
    );
    this.connections.push(connection);
    if (this.autoOpen) queueMicrotask(() => connection.open());
    return connection;
  }

  open(index = this.connections.length - 1): void {
    this.connections[index]?.open();
  }

  /** Send a server-issued session_started message to the selected connection. */
  sessionStarted(
    proxySessionId: string,
    options: {
      resumed?: boolean;
      totalBytes?: number;
      sampleRate?: number;
      index?: number;
    } = {},
  ): void {
    const connection = this.connections[options.index ?? this.connections.length - 1];
    if (!connection) return;
    const session = this.sessions.get(proxySessionId) ?? {
      totalBytes: options.totalBytes ?? 0,
      ended: false,
    };
    session.totalBytes = options.totalBytes ?? session.totalBytes;
    session.ended = false;
    session.attached = connection;
    this.sessions.set(proxySessionId, session);
    connection.setProxySessionId(proxySessionId);
    connection.activateProxySession();
    connection.receive(
      JSON.stringify({
        type: "session_started",
        session_id: proxySessionId,
        resumed: options.resumed ?? false,
        server_time: new Date(0).toISOString(),
        sample_rate: options.sampleRate ?? 16_000,
        total_bytes: session.totalBytes,
      }),
    );
  }

  acknowledge(
    sequenceNumber: number,
    totalBytes?: number,
    index = this.connections.length - 1,
  ): void {
    const connection = this.connections[index];
    if (!connection) return;
    const session = connection.proxySessionId
      ? this.sessions.get(connection.proxySessionId)
      : undefined;
    connection.receive(
      JSON.stringify({
        type: "ack",
        seq: sequenceNumber,
        total_bytes: totalBytes ?? session?.totalBytes ?? 0,
      }),
    );
  }

  endSession(index = this.connections.length - 1): void {
    const connection = this.connections[index];
    const proxySessionId = connection?.proxySessionId;
    if (!connection || !proxySessionId || !connection.isProxySessionActive) return;
    const session = this.sessions.get(proxySessionId);
    if (!session) return;
    session.ended = true;
    session.attached = undefined;
    connection.receive(
      JSON.stringify({
        type: "session_ended",
        session_id: proxySessionId,
        total_bytes: session.totalBytes,
        chunk_count: 0,
      }),
    );
    queueMicrotask(() => connection.disconnect({ code: 1000, reason: "session ended" }));
  }

  sendError(
    code: string,
    message = code,
    index = this.connections.length - 1,
  ): void {
    this.connections[index]?.receive(
      JSON.stringify({ type: "error", code, message }),
    );
  }

  disconnect(
    event: AudioProxyCloseEvent = { code: 1006, reason: "test disconnect" },
    index = this.connections.length - 1,
  ): void {
    this.connections[index]?.disconnect(event);
  }

  fail(
    error = new Error("Mock audio proxy failure"),
    index = this.connections.length - 1,
  ): void {
    this.connections[index]?.fail(error);
  }

  setBufferedAmount(amount: number, index = this.connections.length - 1): void {
    this.connections[index]?.setBufferedAmount(amount);
  }

  private recordMessage(connection: MockAudioProxyConnection, message: string): void {
    const parsed = parseJsonObject(message);
    const type = typeof parsed?.type === "string" ? parsed.type : undefined;
    const proxySessionId =
      typeof parsed?.session_id === "string"
        ? parsed.session_id
        : connection.proxySessionId;
    const sequenceNumber = typeof parsed?.seq === "number" ? parsed.seq : undefined;
    const framePayload = typeof parsed?.frames === "string" ? parsed.frames : undefined;
    const metadata: MockMessageMetadata = {
      type,
      proxySessionId,
      sequenceNumber,
      byteLength: framePayload ? base64ByteLength(framePayload) : undefined,
    };
    this.messages.push(metadata);

    if (!this.autoSessionStarted || !type) return;
    switch (type) {
      case "start_session":
        this.handleStartSession(connection, parsed!);
        break;
      case "audio":
        this.handleAudio(connection, framePayload);
        break;
      case "end_session":
        queueMicrotask(() => this.endSession(this.connections.indexOf(connection)));
        break;
      default:
        this.reject(connection, "malformed_message", "unrecognized message shape", false);
    }
  }

  private handleStartSession(
    connection: MockAudioProxyConnection,
    message: Record<string, unknown>,
  ): void {
    if (connection.proxySessionId) {
      this.reject(connection, "protocol_violation", "session already started on this socket");
      return;
    }
    const key = typeof message.key === "string" ? message.key : "";
    if (!key || (this.expectedApiKey !== undefined && key !== this.expectedApiKey)) {
      this.reject(connection, "unauthorized", "invalid key");
      return;
    }
    if (message.sample_rate !== undefined && message.sample_rate !== 16_000) {
      this.reject(connection, "sample_rate_mismatch", "server records at 16000 Hz");
      return;
    }

    const requestedId =
      typeof message.session_id === "string" ? message.session_id : undefined;
    let proxySessionId = requestedId;
    let session = requestedId ? this.sessions.get(requestedId) : undefined;
    if (requestedId && (!session || session.ended)) {
      this.reject(connection, "unknown_session", "session is unknown or has already been finalized");
      return;
    }
    if (session?.attached && session.attached !== connection) {
      this.reject(connection, "session_in_use", "that session already has a live connection");
      return;
    }
    if (!session) {
      proxySessionId = this.sessionIdFactory();
      session = { totalBytes: 0, ended: false };
      this.sessions.set(proxySessionId, session);
    }
    session.attached = connection;
    connection.setProxySessionId(proxySessionId!);
    queueMicrotask(() =>
      this.sessionStarted(proxySessionId!, {
        resumed: Boolean(requestedId),
        totalBytes: session!.totalBytes,
        index: this.connections.indexOf(connection),
      }),
    );
  }

  private handleAudio(
    connection: MockAudioProxyConnection,
    framePayload: string | undefined,
  ): void {
    const proxySessionId = connection.proxySessionId;
    const session = proxySessionId ? this.sessions.get(proxySessionId) : undefined;
    if (!session || !connection.isProxySessionActive) {
      this.reject(connection, "protocol_violation", "audio received before start_session");
      return;
    }
    const bytes = framePayload ? base64ByteLength(framePayload) : 0;
    if (!framePayload || bytes === 0 || bytes % 4 !== 0) {
      this.sendError("bad_audio_frame", "invalid base64 Float32 frame", this.connections.indexOf(connection));
      return;
    }
    session.totalBytes += bytes;
  }

  private reject(
    connection: MockAudioProxyConnection,
    code: string,
    message: string,
    close = true,
  ): void {
    this.sendError(code, message, this.connections.indexOf(connection));
    if (close) queueMicrotask(() => connection.disconnect({ code: 1008, reason: code }));
  }

  private detach(connection: MockAudioProxyConnection): void {
    const proxySessionId = connection.proxySessionId;
    const session = proxySessionId ? this.sessions.get(proxySessionId) : undefined;
    if (session?.attached === connection && !session.ended) session.attached = undefined;
  }
}

export class MockAudioProxyConnection implements AudioProxyConnection {
  private amount = 0;
  private opened = false;
  private closed = false;
  private sessionId?: string;
  private sessionActive = false;

  constructor(
    private readonly handlers: AudioProxyConnectionHandlers,
    private readonly onSend: (message: string) => void,
    private readonly onClose: () => void,
  ) {}

  get bufferedAmount(): number {
    return this.amount;
  }

  get proxySessionId(): string | undefined {
    return this.sessionId;
  }

  get isProxySessionActive(): boolean {
    return this.sessionActive;
  }

  setProxySessionId(proxySessionId: string): void {
    this.sessionId = proxySessionId;
  }

  activateProxySession(): void {
    this.sessionActive = true;
  }

  send(message: string): void {
    if (this.closed) throw new Error("Mock audio proxy connection is closed.");
    this.onSend(message);
  }

  close(code?: number, reason?: string): void {
    this.disconnect({ code, reason, wasClean: true });
  }

  open(): void {
    if (this.opened || this.closed) return;
    this.opened = true;
    this.handlers.onOpen();
  }

  receive(message: string): void {
    if (!this.closed) this.handlers.onMessage(message);
  }

  disconnect(event: AudioProxyCloseEvent): void {
    if (this.closed) return;
    this.closed = true;
    this.onClose();
    this.handlers.onClose(event);
  }

  fail(error: Error): void {
    if (!this.closed) this.handlers.onError(error);
  }

  setBufferedAmount(amount: number): void {
    this.amount = Math.max(0, amount);
  }
}

function base64ByteLength(encoded: string): number {
  const padding = encoded.endsWith("==") ? 2 : encoded.endsWith("=") ? 1 : 0;
  return Math.max(0, Math.floor((encoded.length * 3) / 4) - padding);
}
